import ExternalLink from "@/components/shared/ExternalLink";
import {
  Colors,
  getBrowserString,
  isBrowserCompatible
} from "@/components/shared/util";
import { navigationNamespace } from "@/store/modules/navigation";
import { patientNamespace } from "@/store/modules/patient";
import { Patient } from "@/store/modules/patient/types";
import { userNamespace } from "@/store/modules/user";
import _ from "lodash";
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";

@Component({ components: { ExternalLink } })
export default class App extends Vue {
  @Getter("towerMode", patientNamespace)
  towerMode!: boolean;

  @Getter("area", patientNamespace)
  area!: string;

  @Getter("isLoggedIn", userNamespace)
  isLoggedIn!: boolean;

  @Action("setSpeechSynthesisEnabled", userNamespace)
  setSpeechSynthesisEnabled!: Function;

  @Action("darkModeOs", navigationNamespace)
  darkModeOs!: Function;

  @Getter("staticTitle", navigationNamespace)
  staticTitle!: boolean;

  @Getter("config", patientNamespace)
  config!: any;

  @Getter("darkMode", navigationNamespace)
  dark!: boolean;

  @Action("showPopup", navigationNamespace)
  showPopup!: Function;

  @Getter("patients", patientNamespace)
  patients!: Array<Patient>;

  @Getter("current", patientNamespace)
  patient!: Patient;

  @Getter("systemLoaded", userNamespace)
  systemLoaded!: boolean;

  @Action("system", userNamespace)
  system!: Function;

  @Watch("area", { immediate: true })
  async updateArea() {
    const dark =
      window.matchMedia != null &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;

    this.setFavicon(dark, this.area);
  }

  get isReady() {
    return this.isLoggedIn && this.systemLoaded && this.varsLoaded;
  }

  get LogoBlue() {
    return Colors.LogoBlue;
  }

  @Watch("$route.path")
  @Watch("towerMode")
  @Watch("patient")
  async updateTitle() {
    let title = "AlertWatch";
    if (this.staticTitle !== true) {
      if (this.area != null && this.area != "") {
        title += ":" + this.area.replace("aw", "").toUpperCase();
      }
      if (this.towerMode) {
        title += " Tower";
      }
      if (this.$route.path.startsWith("/" + this.area + "/help")) {
        const tabs = _.get(this, "config.help.tabs", []);
        const path = this.$route.path.split("/");
        if (path.length > 3) {
          const helpKey = path[3];
          const helpPages = tabs.filter((f: any) => f.key == helpKey);
          title += " - " + (helpPages.length > 0 ? helpPages[0].title : "Help");
        } else {
          title += " - Help";
        }
      } else if (this.$route.name === "census") {
        title += " - " + this.$route.params.unit;
      } else if (this.$route.name === "body") {
        const displayRoom = _.get(this, "patient.census.displayRoom", "");
        if (displayRoom != "") {
          title += " - " + displayRoom;
        }
      } else if (this.$route.name === "status") {
        title += " - " + "Status";
      }
      document.title = title;
    } else if (document.title !== "AlertWatch") {
      document.title = "AlertWatch";
    }
  }
  get loginRoute() {
    return this.$route.name === "login";
  }

  colorOrange = Colors.Orange;

  func = () => {
    this.setOsTheme();
  };

  created() {
    this.initialize();
  }

  @Watch("systemLoaded")
  initialize() {
    // For logout => login path
    if (!this.systemLoaded) {
      this.system();
    }
  }

  @Getter("areas", userNamespace)
  areas!: Array<string>;
  get readyForVarRequests() {
    return (
      this.systemLoaded && this.isLoggedIn && this.areas && this.areas.length
    );
  }

  @Action("getAllVars", patientNamespace)
  getAllVars!: Function;

  @Getter("varsLoaded", patientNamespace)
  varsLoaded!: boolean;

  @Watch("readyForVarRequests")
  async fetchVars() {
    if (this.readyForVarRequests) {
      this.getAllVars();
    }
  }

  get showRoute() {
    return this.isReady || this.loginRoute || this.productSelectRoute;
  }

  get productSelectRoute() {
    return this.$route.name === "productSelect";
  }

  mounted() {
    this.showIncompatiblePopup = false;
    setTimeout(() => {
      this.showIncompatiblePopup = !isBrowserCompatible();
    }, 2000);

    this.updateTitle();

    window.awCommunicationChannel = {
      openedWindows: {}
    };

    window.addEventListener("keypress", this.keyPress);
    window.onkeydown = this.keyPress;

    this.setOsTheme();
    this.attachThemeListeners();
  }

  beforeDestroy() {
    this.detachThemeListeners();
  }

  attachThemeListeners() {
    if (window.matchMedia != null) {
      // Chrome
      if (
        window.matchMedia("(prefers-color-scheme: dark)").addEventListener !=
        null
      ) {
        window
          .matchMedia("(prefers-color-scheme: dark)")
          .addEventListener("change", this.func);
      }
      // Safari
      else if (
        window.matchMedia("(prefers-color-scheme: dark)").addListener != null
      ) {
        window
          .matchMedia("(prefers-color-scheme: dark)")
          .addListener(this.func);
      }
    }
  }

  detachThemeListeners() {
    if (window.matchMedia != null) {
      //Chrome
      if (
        window.matchMedia("(prefers-color-scheme: dark)").addEventListener !=
        null
      ) {
        window.removeEventListener("change", this.func);
      }
      // Safari
      else if (
        window.matchMedia("(prefers-color-scheme: dark)").addListener != null
      ) {
        window
          .matchMedia("(prefers-color-scheme: dark)")
          .removeListener(this.func);
      }
    }
  }

  async setOsTheme() {
    if (window.matchMedia != null) {
      if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
        this.darkModeOs(true);
        this.setFavicon(true, this.area);
      } else if (window.matchMedia("(prefers-color-scheme: light)").matches) {
        this.darkModeOs(false);
        this.setFavicon(false, this.area);
      }
    }
  }

  async setFavicon(dark: boolean, area: string = "") {
    if (
      document != null &&
      document.querySelector != null &&
      document.querySelector("link[rel*='icon']") != null
    ) {
      let dqs = document.querySelector("link[rel*='icon']");
      if (dqs != null) {
        const favicon =
          "favicon" +
          (dark ? "-dark" : "") +
          (area !== "" ? "-" + area : "") +
          ".ico";
        dqs.setAttribute("href", favicon);
      }
    }
  }

  async keyPress(e: any) {
    const path = this.$route.path;
    const isHelp = path.startsWith("/" + this.area + "/help");
    if (!path.startsWith("/login")) {
      if (e.key === "Home" && !isHelp) {
        this.$router.push({ path: "/" + this.area + "/help" });
      } else if (e.key === "Escape") {
        this.$router.back();
      } else if (e.key === "End" && !isHelp) {
        this.showPopup("Settings");
      } else if (path.startsWith("/" + this.area + "/patient")) {
        if (e.key == "PageDown") {
          this.$router.push({
            path: "/" + this.area + "/patient/" + this.NextPt
          });
        } else if (e.key == "Delete") {
          this.$router.push({
            path: "/" + this.area + "/patient/" + this.PrevPt
          });
        }
      }
    }
  }

  get currentPtIx() {
    const path = this.$route.path;
    if (
      path.startsWith("/" + this.area + "/patient") &&
      this.patients != null
    ) {
      const currentPt = path
        .replace("/" + this.area + "/patient/", "")
        .split("?")[0];
      const ix = this.patients.findIndex(
        (pt: Patient) => pt.census.roomSys.toString() === currentPt
      );
      return ix;
    } else {
      return -1;
    }
  }

  get NextPt() {
    const path = this.$route.path;
    if (
      path.startsWith("/" + this.area + "/patient") &&
      this.currentPtIx >= 0
    ) {
      return this.currentPtIx + 1 < this.patients.length
        ? this.patients[this.currentPtIx + 1].census.roomSys
        : -1;
    } else {
      return -1;
    }
  }

  get PrevPt() {
    const path = this.$route.path;
    if (path.startsWith("/" + this.area + "/patient") && this.currentPtIx > 0) {
      return this.currentPtIx > 0
        ? this.patients[this.currentPtIx - 1].census.roomSys
        : -1;
    } else {
      return -1;
    }
  }

  showIncompatiblePopup = false;
  get browserText() {
    return getBrowserString();
  }

  compatibleBrowsers = [
    {
      name: "Chrome",
      iconPath: "./img/icons/browserChrome.svg",
      url: "https://www.google.com/chrome/"
    },
    {
      name: "Edge",
      iconPath: "./img/icons/browserEdge.svg",
      url: "https://www.microsoft.com/en-us/edge"
    },
    // {
    //   name: "Firefox",
    //   iconPath: "./img/icons/browserFirefox.svg",
    //   url: "https://www.mozilla.org/en-US/firefox/"
    // },
    {
      name: "Safari",
      iconPath: "./img/icons/browserSafari.svg",
      url: "https://www.apple.com/safari/"
    }
  ];
}
